import React, { useState, useEffect } from 'react';
import { PageSection, SiteData, YachtMarina } from '../../../types';
import { dbService } from '../../../services/firebase';
import { MapPin, Anchor, ShieldCheck, Check, Phone } from 'lucide-react';
import { isDarkBackground, getOptimalTextColor, getOptimalMutedTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
}

export const YachtMarinasSection: React.FC<SectionProps> = ({
  section,
  siteData,
  onNavigate,
}) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'BAĞLAMA LİMANLARI & MARİNALAR';
  const title = content.title || 'Lüks Filonuz İçin Ana Çıkış ve Bağlama Noktaları';
  const description =
    content.description ||
    'Uluslararası standartlarda modern hizmetler sunan marinalardan güvenli ve konforlu şekilde seyre başlayın.';

  const [marinas, setMarinas] = useState<YachtMarina[]>([]);

  useEffect(() => {
    let isMounted = true;
    const fetchMarinas = async () => {
      try {
        const data = await dbService.getMarinas(siteData.id);
        if (isMounted) {
          setMarinas(data || []);
        }
      } catch (err) {
        console.warn('Marinas load fallback:', err);
      }
    };
    fetchMarinas();
    return () => {
      isMounted = false;
    };
  }, [siteData.id]);

  const bgColor = style.backgroundColor || '#F8FAFC';
  const isDark = isDarkBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const mutedTextColor = getOptimalMutedTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#0284C7';

  return (
    <section
      className="py-20 px-4 sm:px-6 lg:px-8 select-none"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          {badge && (
            <div
              className={`inline-block px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${
                isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-500/10 text-cyan-700'
              }`}
            >
              {badge}
            </div>
          )}
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight">{title}</h2>
          {description && (
            <p className="text-sm sm:text-base leading-relaxed max-w-2xl mx-auto" style={{ color: mutedTextColor }}>
              {description}
            </p>
          )}
        </div>

        {/* Marinas Grid */}
        {marinas.length === 0 ? (
          <div className="text-center py-12 px-4 rounded-3xl border border-dashed border-slate-500/20 text-slate-400 max-w-xl mx-auto">
            <Anchor className="w-8 h-8 mx-auto mb-2 opacity-50 text-cyan-500" />
            <p className="text-sm font-medium">Henüz bağlama limanı veya marina tanımlanmadı.</p>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {marinas.map((marina) => (
            <div
              key={marina.id}
              className={`rounded-3xl overflow-hidden border shadow-md hover:shadow-xl transition duration-300 flex flex-col justify-between ${
                isDark
                  ? 'bg-slate-900 border-white/10 text-white'
                  : 'bg-white border-slate-200/80 text-slate-900'
              }`}
            >
              <div className="relative h-60 overflow-hidden bg-slate-900">
                <img
                  src={marina.coverImage}
                  alt={marina.name}
                  className="w-full h-full object-cover hover:scale-105 transition duration-500"
                />
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-xl bg-slate-900/80 backdrop-blur-md text-white text-xs font-bold flex items-center gap-1.5 shadow">
                  <MapPin className="w-3.5 h-3.5 text-cyan-400" />
                  <span>{marina.location}</span>
                </div>
                {marina.coordinates && (
                  <div className="absolute bottom-4 right-4 px-2.5 py-1 rounded-lg bg-slate-900/70 backdrop-blur-xs text-[11px] font-mono text-cyan-300">
                    {marina.coordinates}
                  </div>
                )}
              </div>

              <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className={`text-xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{marina.name}</h3>
                  <p className={`text-xs mt-1 leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{marina.description}</p>
                </div>

                {/* Specs */}
                <div className={`grid grid-cols-3 gap-2 py-3 border-y text-center text-xs ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
                  <div>
                    <span className="text-slate-400 block text-[10px]">Bağlama Kapasitesi</span>
                    <span className={`font-extrabold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{marina.berthCapacity || 400}+ Yat</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px]">Maksimum Boy</span>
                    <span className={`font-extrabold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{marina.maxLoa || '70m'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px]">Maksimum Derinlik</span>
                    <span className={`font-extrabold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{marina.maxDraft || '6m'}</span>
                  </div>
                </div>

                {/* Services */}
                {marina.services && marina.services.length > 0 && (
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      Marina Hizmetleri & Tesisler
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {marina.services.map((srv, idx) => (
                        <span
                          key={idx}
                          className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium border ${
                            isDark
                              ? 'bg-cyan-950/40 text-cyan-300 border-cyan-800/40'
                              : 'bg-cyan-50 text-cyan-800 border-cyan-100'
                          }`}
                        >
                          ✓ {srv}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => onNavigate && onNavigate('/iletisim')}
                    className={`w-full py-2.5 px-4 rounded-xl border font-bold text-xs flex items-center justify-center gap-2 transition ${
                      isDark
                        ? 'border-white/20 hover:bg-white/10 text-white'
                        : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <Anchor className="w-3.5 h-3.5 text-cyan-500" />
                    <span>Liman ve Transfer Bilgisi Al</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </section>
  );
};
